import { useEffect, useMemo, useState } from "react";
import { Button, Modal, message } from "antd";
import { useRecoilState } from "recoil";
import { brandKitsState } from "../../../../state/atoms";
import AssetApi from "../../../../api/AssetApi";
import { AssetDto, BrandKitDto } from "../../../../generated/dashboard-client";
import BrandKitMultiSelect from "./tabs/BrandKitMultiSelect";
import "./AddToBrandKitModal.scss";

interface AddToBrandKitModalProps {
  open: boolean;
  currentBrandKitId: string;
  currentBrandKitName?: string;
  assets: AssetDto[];
  onClose: () => void;
  onAdded?: (kitNames: string[]) => void;
}

export function AddToBrandKitModal({
  open,
  currentBrandKitId,
  currentBrandKitName,
  assets,
  onClose,
  onAdded,
}: AddToBrandKitModalProps) {
  const [brandKits, setBrandKits] = useRecoilState(brandKitsState);
  const [selected, setSelected] = useState<string[]>([]);
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    if (open) {
      setSelected([]);
    }
  }, [open]);

  const total = assets.length;
  const assetIds = useMemo(
    () => assets.map((a) => a.id!).filter(Boolean),
    [assets]
  );

  const otherKits = useMemo(
    () => brandKits.filter((b) => b.id && b.id !== currentBrandKitId),
    [brandKits, currentBrandKitId]
  );

  const alreadyCountFor = (name?: string) =>
    name ? assets.filter((a) => a.brandKitNames?.includes(name)).length : 0;

  const isFullyHere = (kit: BrandKitDto) => total > 0 && alreadyCountFor(kit.name) === total;

  const alreadyHereLabel = (kit: BrandKitDto) => {
    const already = alreadyCountFor(kit.name);
    if (already === 0) return undefined;
    return already === total ? "Already here" : `${already} of ${total} already here`;
  };

  function close() {
    if (adding) return;
    onClose();
  }

  function handleAdd() {
    if (selected.length === 0 || assetIds.length === 0) return;
    setAdding(true);

    const targets = otherKits.filter((b) => selected.includes(b.id!));

    AssetApi.assignToBrands(assetIds, targets.map((b) => b.id!))
      .then(() => {
        setBrandKits((prev) =>
          prev.map((b) => {
            if (!selected.includes(b.id!)) return b;
            const added = total - alreadyCountFor(b.name);
            return added > 0
              ? ({ ...b, assetCount: ((b as any).assetCount ?? 0) + added } as any)
              : b;
          })
        );
        const names = targets.map((b) => b.name || "Untitled");
        const noun = `asset${total === 1 ? "" : "s"}`;
        const kitNoun = `Brand Kit${names.length === 1 ? "" : "s"}`;
        message.success(`${total} ${noun} added to ${names.length} ${kitNoun}`);
        onAdded?.(names);
        onClose();
      })
      .catch(() => message.error("Failed to add to Brand Kit"))
      .finally(() => setAdding(false));
  }

  return (
    <Modal
      className="add-to-brand-kit-modal"
      open={open}
      onCancel={close}
      footer={null}
      width={600}
      destroyOnClose
    >
      <header>
        <h2>Add to other Brand Kit</h2>
        <p>
          Adding {total} asset{total === 1 ? "" : "s"} to another Brand Kit.
          <br />
          They'll stay in {currentBrandKitName || "this Brand Kit"} too.
        </p>
      </header>

      <BrandKitMultiSelect
        kits={otherKits}
        selected={selected}
        onChange={setSelected}
        getMeta={alreadyHereLabel}
        isDisabled={isFullyHere}
      />

      <footer>
        <Button size="large" onClick={close} disabled={adding}>
          Cancel
        </Button>
        <Button
          size="large"
          className="primary"
          onClick={handleAdd}
          loading={adding}
          disabled={selected.length === 0}
        >
          Add
        </Button>
      </footer>
    </Modal>
  );
}
